<template>
    <div class="card p-2 appointment-table">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
                <th>Serial No</th>
                <th>Patient Details</th>
                <th>Schedule Date</th>
                <th>Doctor Name</th>
                <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in tableItems"
                :key="i"
                :style="{
                    backgroundColor: getColor(item.billing_status, 'bg'),
                    color: getColor(item.billing_status, 'text'),
                    }"
            >
                <td>{{ item.service_serial_no }}</td>
                <td>
                    <div class="col">
                        <strong>
                            <p>{{ item.patient_name }} {{ formatPatientInfo(item) }} </p>
                        </strong>
                        {{ formatPatientAdditionalInfo(item) }}
                    </div>
                </td>
                <td>{{ item.start_time }}</td>
                <td>{{ item.service_resource_name }}</td>
                <td>
                    <div v-if="item.sale_master_id" class="d-flex">
                        <button class="btn btn-primary btn-sm"
                            @click="goToEdit(item.sale_master_id)"
                        >
                            <i class="fas fa-eye"></i>
                        </button>

                        <button class="btn btn-secondary btn-sm"
                            @click="goToPrint(item.sale_master_id)"
                            style="cursor: pointer; margin-left: 10px"
                        >
                            <i class="fas fa-print"></i>
                        </button>
                    </div>

                    <div v-else>
                        <button @click="goToBilling(item)" class="btn btn-sm btn-primary"><i class="fa fa-plus"></i> Create Billing</button>
                    </div>
                </td>
            </tr>
          </tbody>
        </table>
        <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
      </div>
    </div>
</template>

<script setup>

import { convertToTitleCase } from '@/services/utils/global.js';
import { useRoute, useRouter } from 'vue-router';
import handlePurchase from "@/services/modules/purchase";
import hospitalPdfPrinterHelper from "@/services/utils/hospitalPdfPrinterHelper";
import { onMounted, ref } from 'vue';
import handleCompany from "@/services/modules/company";

const props = defineProps({
    tableItems: {
        type: Array,
        default: () => []
    }
})

const route = useRoute();
const router = useRouter();
const {fetchSalesBill} = handlePurchase();
const { generatePdf } = hospitalPdfPrinterHelper();
const {fetchCompanyInfo, fetchActiveCustomizationField} = handleCompany();

const invoiceRes = ref({});
const companyInfo = ref({});
const customizationPdfFields = ref({});

const billingStatusColors = {
    'completed': { bg: '#bbf7d0', text: '#166534' },
    'pending':     { bg: '#fde68a', text: '#78350f' }
}
    
const formatPatientInfo = (item) => {
    
    if(!item.patient_birthday && !item.patient_gender) return '';

    let texts = [];
    
    if(item.patient_age){
        texts.push(item.patient_age);
    }
    
    if(item.patient_gender){
        const gender = convertToTitleCase(item.patient_gender);
        texts.push(gender);
    }
    
    return `(${texts.join(', ')})`;
}

const formatPatientAdditionalInfo = (item) => {
    let texts = [];
    if(item.patient_mobile_no) texts.push(item.patient_mobile_no);
    if(item.patient_serial_no) texts.push(item.patient_serial_no);
    return texts.join(' | ');
}

const getColor = (status, type) => {
    if (!(status in billingStatusColors)) {
        return type === 'bg' ? '#f9fafb' : '#111827';
    }
    return billingStatusColors[status][type];
}

const goToBilling = (item) => {

    if (!item.service_general_id) {
        return;
    }

    const params = Object.assign({}, route.params);

    router.push({
        name: 'opd-billing',
        params: params,
        query: {
            serviceGeneralId: item.service_general_id,
            from: 'appointment',
            start: route.query.start,
            end: route.query.end,
        }
    })
}

const goToEdit = (id) => {

    const params = Object.assign({}, route.params);
    params.saleId = id;

    router.push({
        name: 'opd-billing-edit',
        params: params,
        query: {
            from: 'appointment',
            start: route.query.start,
            end: route.query.end,
        }
    })
}

const goToPrint = (id) => {
    const companyQuery = `?company_id=${route.params.companyId}`;

    fetchSalesBill(id, companyQuery).then( (res) => {
        if( res ){
            invoiceRes.value = res.data;
            if (invoiceRes.value.service_general && invoiceRes.value.service_general.service_resource) {
                invoiceRes.value.consultant = invoiceRes.value.service_general.service_resource.name ?? ''
            }
        }
    }).then( () => {
        generatePdf(companyInfo.value, invoiceRes.value, 'BILL', true, true, true, false, false, customizationPdfFields.value);
    }).catch( (err) => {
        console.log(err);
    })
}

const getCompanyInfo = () => {
    fetchCompanyInfo(route.params.companyId).then(res=> {
        if(res.data) companyInfo.value = res.data;
    })
}

const getActiveFiledCustomization = async () => {
  const companyQuery = `?company_id=${route.params.companyId}&customization_type=pdf&type=opd_bill_pdf`;
  try {
    const res = await fetchActiveCustomizationField(companyQuery);
    customizationPdfFields.value = res.data;
  } catch (error) {
    console.error("Error fetching customization fields:", error);
  }
}

onMounted(() => {
    getCompanyInfo();
    getActiveFiledCustomization();
})

</script>

<style scoped>
.appointment-table {
    min-height: 200px;
}
</style>